import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { createSliceWithStateReset } from 'store/createSliceWithStateReset';
import {
  AssessmentQuestionSkipLogicMap,
  QuestionIdToQuestionNumberMap,
  AssessmentStatus,
  AssessmentsPage,
  AssessmentSubmissionState,
} from './types';

interface SubmissionState {
  state: AssessmentSubmissionState | null;
  unansweredQuestion: number | null;
  requiredUnansweredQuestion: number | null;
}

interface InitialState {
  skippedQuestionNumbers: number[];
  assessmentStatus: null | AssessmentStatus;
  skippedQuestions: string[];
  // Question ids with an answer on the server (loaded or last saved), used to detect cleared answers.
  previouslyAnsweredQuestionIds: string[];
  currentPage: number;
  activeQuestionId: number | null;
  skipLogicMap: AssessmentQuestionSkipLogicMap;
  questionIdToNumberMap: QuestionIdToQuestionNumberMap;
  submissionId: string | null;
  saveCompleted: boolean;
  saveAndCloseCompleted: boolean;
  pages: AssessmentsPage[];
  assessmentSubmissionState: SubmissionState;
}

// TODO: The hardcoded values will be available in verify user.
const initialState: InitialState = {
  skippedQuestions: [],
  previouslyAnsweredQuestionIds: [],
  assessmentStatus: null,
  skippedQuestionNumbers: [],
  currentPage: 1,
  activeQuestionId: null,
  skipLogicMap: {},
  questionIdToNumberMap: {},
  submissionId: null,
  saveCompleted: false,
  saveAndCloseCompleted: false,
  pages: [],
  assessmentSubmissionState: {
    state: null,
    unansweredQuestion: null,
    requiredUnansweredQuestion: null,
  },
};

const assessmentsSlice = createSliceWithStateReset({
  name: 'assessments',
  // Using a function here ensures we always fetch the latest localStorage during initialisation.
  initialState,
  reducers: {
    addSkippedQuestions: (state, action: PayloadAction<number[]>) => ({
      ...state,
      skippedQuestionNumbers: [...state.skippedQuestionNumbers, ...action.payload],
    }),
    removeSkippedQuestion: (state, action: PayloadAction<number>) => ({
      ...state,
      skippedQuestionNumbers: state.skippedQuestionNumbers.filter((q) => q !== action.payload),
    }),
    setSkippedQuestions: (state, action: PayloadAction<number[]>) => ({
      ...state,
      skippedQuestionNumbers: action.payload,
    }),
    setPreviouslyAnsweredQuestionIds: (state, action: PayloadAction<string[]>) => ({
      ...state,
      previouslyAnsweredQuestionIds: action.payload,
    }),
    setCurrentPage: (state, action: PayloadAction<number>) => ({
      ...state,
      currentPage: action.payload,
    }),
    setActiveQuestionId: (state, action: PayloadAction<number | null>) => ({
      ...state,
      activeQuestionId: action.payload,
    }),
    setSkipLogicMap: (state, action: PayloadAction<AssessmentQuestionSkipLogicMap>) => ({
      ...state,
      skipLogicMap: action.payload,
    }),
    setQuestionIdToNumberMap: (state, action: PayloadAction<QuestionIdToQuestionNumberMap>) => ({
      ...state,
      questionIdToNumberMap: action.payload,
    }),
    setAssessmentStatus: (state, action: PayloadAction<AssessmentStatus>) => ({
      ...state,
      assessmentStatus: action.payload,
    }),
    setSubmissionId: (state, action: PayloadAction<string>) => ({
      ...state,
      submissionId: action.payload,
    }),
    setSaveCompleted: (state, action: PayloadAction<boolean>) => ({
      ...state,
      saveCompleted: action.payload,
    }),
    setSaveAndCloseCompleted: (state, action: PayloadAction<boolean>) => ({
      ...state,
      saveAndCloseCompleted: action.payload,
    }),
    setPages: (state, action: PayloadAction<AssessmentsPage[]>) => ({
      ...state,
      pages: action.payload,
    }),
    setAssessmentSubmissionState: (state, action: PayloadAction<SubmissionState>) => ({
      ...state,
      assessmentSubmissionState: action.payload,
    }),
    clearAssessments: () => initialState,
  },
});

export const {
  clearAssessments,
  setSkippedQuestions,
  setPreviouslyAnsweredQuestionIds,
  addSkippedQuestions,
  removeSkippedQuestion,
  setCurrentPage,
  setAssessmentStatus,
  setActiveQuestionId,
  setSkipLogicMap,
  setQuestionIdToNumberMap,
  setSubmissionId,
  setSaveCompleted,
  setSaveAndCloseCompleted,
  setPages,
  setAssessmentSubmissionState,
} = assessmentsSlice.actions;

export const { reducer: assessmentsReducer } = assessmentsSlice;

export const selectSkippedQuestionNumbers = (state: RootState) =>
  state.assessments.skippedQuestionNumbers;

export const selectPreviouslyAnsweredQuestionIds = (state: RootState) =>
  state.assessments.previouslyAnsweredQuestionIds;

export const selectCurrentPage = (state: RootState) => state.assessments.currentPage;

export const selectSkipLogicMap = (state: RootState) => state.assessments.skipLogicMap;

export const selectQuestionIdToNumberMap = (state: RootState) =>
  state.assessments.questionIdToNumberMap;

export const selectAssessmentStatus = (state: RootState) => state.assessments.assessmentStatus;

export const selectActiveQuestionId = (state: RootState) => state.assessments.activeQuestionId;

export const selectSubmissionId = (state: RootState) => state.assessments.submissionId;

export const selectSaveCompleted = (state: RootState) => state.assessments.saveCompleted;

export const selectSaveAndCloseCompleted = (state: RootState) =>
  state.assessments.saveAndCloseCompleted;

export const selectPages = (state: RootState) => state.assessments.pages;

export const selectAssessmentSubmissionState = (state: RootState) =>
  state.assessments.assessmentSubmissionState;
